/**
 * Intégrations & paramètres — port de admin.integrations.tsx, branché sur
 * /api/admin/parametres : chaque clé est chiffrée en base, l'aperçu ne montre
 * que les 4 premiers caractères, test d'envoi Resend intégré.
 */
import { useEffect, useState } from 'react';
import { appelerApi, envoyer } from '../../reseau.ts';
import { Badge, Btn, Card, PageTitle } from '../ui.tsx';

type Parametre = {
  cle: string;
  description: string;
  sensible: boolean;
  configure: boolean;
  apercu: string | null;
};

const NOMS: Record<string, { nom: string; role: string; test?: boolean }> = {
  smtp_hote: { nom: 'SMTP — serveur', role: 'Envoi des e-mails par votre hébergeur (Mailcheap). Prioritaire sur Resend dès qu’il est renseigné.', test: true },
  smtp_port: { nom: 'SMTP — port', role: '465 (TLS) ou 587 (STARTTLS).' },
  smtp_utilisateur: { nom: 'SMTP — identifiant', role: 'En général l’adresse expéditrice complète.' },
  smtp_mot_de_passe: { nom: 'SMTP — mot de passe', role: 'Le mot de passe de la boîte ou le mot de passe d’application.' },
  smtp_securite: { nom: 'SMTP — sécurité', role: 'tls, starttls ou aucune.' },
  resend_api_key: { nom: 'Resend', role: "Envoi des e-mails transactionnels et d'automatisation (repli si aucun SMTP).", test: true },
  email_expediteur: { nom: 'Expéditeur e-mail', role: 'Adresse « from » de toutes les chaînes automatiques.' },
  deepseek_api_key: { nom: 'DeepSeek', role: 'IA de génération courante (fiches, e-mails sur-mesure).' },
  anthropic_api_key: { nom: 'Anthropic / Claude', role: 'IA des tâches à fort enjeu (conventions, analyses).' },
  stripe_secret_key: { nom: 'Stripe — clé secrète', role: 'Paiements de la plateforme et virements Connect.' },
  stripe_webhook_secret: { nom: 'Stripe — secret webhook', role: 'Signature des événements de paiement entrants.' },
  google_client_id: { nom: 'Google — identifiant client OAuth', role: 'Connexion Google Agenda des entrepreneurs. URI de redirection à déclarer : https://eventmatch.pro/api/agenda/google/retour' },
  google_client_secret: { nom: 'Google — secret client OAuth', role: 'Le secret du même client OAuth (console Google Cloud).' },
  vapid_cle_publique: { nom: 'Notifications push — clé publique', role: 'Générée automatiquement par la plateforme.' },
  vapid_cle_privee: { nom: 'Notifications push — clé privée', role: 'Générée automatiquement par la plateforme.' },
};

export function Integrations() {
  const [parametres, setParametres] = useState<Parametre[]>([]);
  const [valeurs, setValeurs] = useState<Record<string, string>>({});
  const [emailTest, setEmailTest] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const charger = async () => {
    const r = await appelerApi<{ parametres: Parametre[] }>('/api/admin/parametres');
    if (r.donnees) setParametres(r.donnees.parametres);
  };

  useEffect(() => {
    void charger();
  }, []);

  const enregistrer = async (cle: string) => {
    const valeur = valeurs[cle]?.trim();
    if (!valeur) return;
    const r = await envoyer('/api/admin/parametres', { cle, valeur }, 'PUT');
    setMessage(r.erreur ?? `${NOMS[cle]?.nom ?? cle} enregistré.`);
    setValeurs((v) => ({ ...v, [cle]: '' }));
    void charger();
  };

  const testerEmail = async () => {
    const r = await envoyer<{ envoye: boolean; erreur?: string }>('/api/admin/emails/test', {
      destinataire: emailTest.trim(),
    });
    setMessage(r.donnees?.envoye ? 'E-mail de test envoyé ✓' : (r.donnees?.erreur ?? r.erreur ?? 'Échec'));
  };

  return (
    <>
      <PageTitle
        title="Intégrations & paramètres"
        subtitle="Les clés sont chiffrées en base (AES-256-GCM) et ne sont jamais réaffichées en clair — seul un aperçu de 4 caractères reste visible."
      />

      {message && (
        <p className="mb-4 text-[13px] text-fumee" role="status">
          {message}
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {parametres.map((p) => {
          const infos = NOMS[p.cle] ?? { nom: p.cle, role: p.description };
          const generee = p.cle.startsWith('vapid_');
          return (
            <Card key={p.cle}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-[18px] font-light text-blanc">{infos.nom}</h2>
                  <p className="mt-1 text-[14px] text-fumee">{infos.role}</p>
                </div>
                {p.configure ? <Badge filled>Configuré ✓</Badge> : <Badge>Non configuré</Badge>}
              </div>

              {p.configure && p.apercu && (
                <p className="mt-4 font-mono text-[13px] text-fumee">{p.apercu}</p>
              )}

              {!generee && (
                <>
                  <div className="mt-4 flex flex-wrap items-end gap-3">
                    <label className="min-w-[200px] flex-1">
                      <span className="label-caps block text-fumee">
                        {p.sensible ? 'Clé API' : 'Valeur'}
                      </span>
                      <input
                        type={p.sensible ? 'password' : 'text'}
                        placeholder={p.sensible ? '••••••••••••' : 'EventMatch <bonjour@eventmatch.pro>'}
                        value={valeurs[p.cle] ?? ''}
                        onChange={(e) => setValeurs((v) => ({ ...v, [p.cle]: e.target.value }))}
                        className="field-input mt-2 !min-h-[42px] !py-2 !text-sm"
                      />
                    </label>
                    <Btn variant="primary" disabled={!valeurs[p.cle]?.trim()} onClick={() => void enregistrer(p.cle)}>
                      Enregistrer
                    </Btn>
                  </div>
                  <p className="mt-3 text-[13px] text-fumee">
                    {p.sensible ? 'Chiffré en base.' : 'Stocké en base, modifiable à tout moment.'}
                  </p>
                </>
              )}

              {infos.test && (
                <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-graphite pt-4">
                  <label className="min-w-[200px] flex-1">
                    <span className="label-caps block text-fumee">Vérifier la configuration</span>
                    <input
                      type="email"
                      placeholder="votre@email.be"
                      value={emailTest}
                      onChange={(e) => setEmailTest(e.target.value)}
                      className="field-input mt-2 !min-h-[42px] !py-2 !text-sm"
                    />
                  </label>
                  <Btn disabled={!emailTest.trim()} onClick={() => void testerEmail()}>
                    Envoyer un e-mail de test
                  </Btn>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </>
  );
}
